import { getFire, setFire } from "../firestore/utils";

// The service every legacy job (no `serviceType`) resolves to.
export const DEFAULT_SERVICE = "concrete-testing";

export const SETTINGS_DOC = { collection: "settings", doc: "customFields" };

export const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "select", label: "Dropdown" },
  { value: "checkbox", label: "Yes / No" },
  { value: "textarea", label: "Long text" },
];

export const SURFACES = [
  { key: "schedule", label: "Schedule" },
  { key: "jobCard", label: "Job Card" },
  { key: "clientPortal", label: "Client Portal" },
  { key: "tester", label: "Tester" },
];

const showOn = (schedule, jobCard, clientPortal, tester) => ({
  schedule,
  jobCard,
  clientPortal,
  tester,
});

// Fallback used when `settings/customFields` is missing or unreadable. This is
// the seed data — an admin edits it in Settings, which then persists a copy.
export const SERVICE_DEFAULTS = {
  enabled: false,
  services: [
    {
      key: "concrete-testing",
      label: "Concrete Testing",
      order: 0,
      active: true,
      fields: [
        // Locked: these keys already exist on every job doc and are read by the
        // schedule, CSV export, client portal and tester notifications.
        { key: "mixCode", label: "Mix Code", type: "text", locked: true, showOn: showOn(true, true, true, true) },
        { key: "meters", label: "Meters (m³)", type: "text", locked: true, showOn: showOn(true, true, true, true) },
        // Not a schedule column today — must stay false, see initColumnConfig.
        { key: "pourLocation", label: "Pour Location", type: "text", locked: true, showOn: showOn(false, true, true, true) },
      ],
    },
    {
      key: "concrete-coring",
      label: "Concrete Coring",
      order: 1,
      active: true,
      fields: [
        { key: "cf_coreDiameter", label: "Core Diameter (mm)", type: "number", showOn: showOn(true, true, true, true) },
        { key: "cf_numberOfCores", label: "Number of Cores", type: "number", showOn: showOn(true, true, true, true) },
        { key: "cf_coreDepth", label: "Core Depth (mm)", type: "number", showOn: showOn(false, true, true, true) },
        { key: "cf_slabThickness", label: "Slab Thickness (mm)", type: "number", showOn: showOn(false, true, true, true) },
        { key: "cf_reinforcementScanRequired", label: "Reinforcement Scan Required", type: "checkbox", showOn: showOn(false, true, true, true) },
        { key: "cf_waterSupplyAvailable", label: "Water Supply Available", type: "checkbox", showOn: showOn(false, true, false, true) },
        { key: "cf_coreLocation", label: "Core Location", type: "text", showOn: showOn(false, true, true, true) },
      ],
    },
    {
      key: "concrete-cutting",
      label: "Concrete Cutting",
      order: 2,
      active: true,
      fields: [
        { key: "cf_cutType", label: "Cut Type", type: "select", options: ["Saw", "Wall", "Wire", "Ring", "Hand"], showOn: showOn(true, true, true, true) },
        { key: "cf_cutDepth", label: "Cut Depth (mm)", type: "number", showOn: showOn(true, true, true, true) },
        { key: "cf_cutLength", label: "Cut Length (m)", type: "number", showOn: showOn(false, true, true, true) },
        { key: "cf_surfaceType", label: "Surface Type", type: "select", options: ["Slab", "Wall", "Road", "Footpath"], showOn: showOn(false, true, true, true) },
        { key: "cf_powerAvailable", label: "Power Available", type: "checkbox", showOn: showOn(false, true, false, true) },
        { key: "cf_slurryManagement", label: "Slurry Management", type: "text", showOn: showOn(false, true, true, true) },
      ],
    },
    {
      key: "concrete-scanning",
      label: "Concrete Scanning",
      order: 3,
      active: true,
      fields: [
        { key: "cf_scanMethod", label: "Scan Method", type: "select", options: ["GPR", "EM", "Dual"], showOn: showOn(true, true, true, true) },
        { key: "cf_scanArea", label: "Scan Area (m²)", type: "number", showOn: showOn(true, true, true, true) },
        { key: "cf_slabThickness", label: "Slab Thickness (mm)", type: "number", showOn: showOn(false, true, true, true) },
        { key: "cf_scanTarget", label: "Target", type: "select", options: ["Rebar", "Post-tension", "Conduit", "Voids"], showOn: showOn(false, true, true, true) },
        { key: "cf_markingRequired", label: "Marking Required", type: "checkbox", showOn: showOn(false, true, true, true) },
        { key: "cf_reportRequired", label: "Report Required", type: "checkbox", showOn: showOn(false, true, true, true) },
      ],
    },
    {
      key: "soil-testing",
      label: "Soil Testing",
      order: 4,
      active: true,
      fields: [
        { key: "cf_testType", label: "Test Type", type: "select", options: ["CBR", "Compaction", "Classification", "Moisture"], showOn: showOn(true, true, true, true) },
        { key: "cf_numberOfTests", label: "Number of Tests", type: "number", showOn: showOn(true, true, true, true) },
        { key: "cf_testDepth", label: "Test Depth (m)", type: "number", showOn: showOn(false, true, true, true) },
        { key: "cf_materialType", label: "Material Type", type: "text", showOn: showOn(false, true, true, true) },
        { key: "cf_layerLift", label: "Layer / Lift", type: "text", showOn: showOn(false, true, true, true) },
        { key: "cf_sampleRetention", label: "Sample Retention", type: "checkbox", showOn: showOn(false, true, false, true) },
      ],
    },
  ],
};

export const cloneDefaults = () => JSON.parse(JSON.stringify(SERVICE_DEFAULTS));

// `getFire` rejects with no argument when the doc is missing, so never inspect
// the error here — any failure falls back to the in-code defaults.
export const loadDefinitions = function () {
  return getFire(SETTINGS_DOC)
    .then((doc) => (Array.isArray(doc && doc.services) ? doc : cloneDefaults()))
    .catch(() => cloneDefaults());
};

export const saveDefinitions = function (defs) {
  return setFire({
    ...SETTINGS_DOC,
    body: {
      enabled: !!defs.enabled,
      services: defs.services,
    },
  });
};

export const resolveService = (job) => (job && job.serviceType) || DEFAULT_SERVICE;

export const findService = function (defs, serviceKey) {
  if (!defs || !Array.isArray(defs.services)) return null;
  return defs.services.find((s) => s.key === serviceKey) || null;
};

export const activeServices = function (defs) {
  if (!defs || !Array.isArray(defs.services)) return [];
  return defs.services
    .filter((s) => s.active !== false)
    .slice()
    .sort((a, b) => (a.order || 0) - (b.order || 0));
};

// Fields for one service, filtered to a surface ('schedule' | 'jobCard' |
// 'clientPortal' | 'tester'). Returns [] for unknown services.
export const fieldsFor = function (defs, serviceKey, surface) {
  const service = findService(defs, serviceKey);
  if (!service || !Array.isArray(service.fields)) return [];
  if (!surface) return service.fields.slice();
  return service.fields.filter((f) => f.showOn && f.showOn[surface]);
};

export const fieldKeysFor = (defs, serviceKey, surface) =>
  fieldsFor(defs, serviceKey, surface).map((f) => f.key);

// The fields for a job's service on `surface`, paired with the job's values and
// filtered down to what is worth showing. Checkboxes only appear when true.
// Returns [] when `defs` is missing, so callers still gate on `enabled`.
export const resolveFieldValues = function (defs, job, surface) {
  const out = [];
  fieldsFor(defs, resolveService(job), surface).forEach((f) => {
    const v = job[f.key];
    if (f.type === "checkbox") {
      if (v === true) out.push({ ...f, value: "Yes" });
      return;
    }
    if (v === undefined || v === null || v === "") return;
    out.push({ ...f, value: v });
  });
  return out;
};

// Every field definition across every service, deduplicated by key (a key such
// as cf_slabThickness can be shared by more than one service).
export const allFieldDefs = function (defs) {
  if (!defs || !Array.isArray(defs.services)) return [];
  const byKey = new Map();
  defs.services.forEach((s) =>
    (s.fields || []).forEach((f) => {
      if (!byKey.has(f.key)) byKey.set(f.key, f);
    })
  );
  return Array.from(byKey.values());
};

// Every field key defined across every service. Used when clearing values that
// no longer belong to a job's service.
export const allFieldKeys = (defs) => allFieldDefs(defs).map((f) => f.key);

// ---------------------------------------------------------------------------
// SMS shortcodes
//
// A custom field's SMS token is its key verbatim: `{cf_coreDiameter}`. The
// locked concrete-testing fields keep the tokens they have always had
// (`{meters}`, `{mixCode}`), so existing templates are unaffected.

// Templates scoped to this show up for every job regardless of service.
export const SERVICE_ALL = "all";

// Expands to every client-visible field for the job's service, so one template
// can cover all five services.
export const JOB_DETAILS_TOKEN = "{jobDetails}";

const escapeToken = (key) => `\\{${key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\}`;

// Shortcodes for the template editor's picker, grouped by service. Only the
// non-locked fields — the locked ones are already in the static shortcode list.
export const smsShortcodeGroups = function (defs) {
  if (!defs || !Array.isArray(defs.services)) return [];
  return activeServices(defs)
    .map((s) => ({
      key: s.key,
      label: s.label,
      codes: (s.fields || [])
        .filter((f) => !f.locked)
        .map((f) => ({ code: `{${f.key}}`, label: f.label })),
    }))
    .filter((g) => g.codes.length);
};

// 'Core Diameter: 100, Number of Cores: 4'
export const jobDetailsSummary = function (defs, job) {
  return resolveFieldValues(defs, job, "clientPortal")
    .map((f) => `${f.label}: ${f.value}`)
    .join(", ");
};

// Substitutes `{cf_*}` and `{jobDetails}` in an SMS body. Tokens for fields the
// job's service does not have resolve to an empty string rather than being left
// in the message. A no-op when the feature is off.
export const applyCustomFieldTokens = function (defs, job, body) {
  let out = String(body || "");
  if (!defs || !defs.enabled) return out;

  out = out.replace(new RegExp(escapeToken("jobDetails"), "g"), jobDetailsSummary(defs, job));

  const visible = new Set(fieldKeysFor(defs, resolveService(job), "clientPortal"));
  allFieldDefs(defs).forEach((f) => {
    let value = "";
    if (visible.has(f.key)) {
      const v = job[f.key];
      if (f.type === "checkbox") value = v === true ? "Yes" : "";
      else if (v !== undefined && v !== null) value = String(v);
    }
    out = out.replace(new RegExp(escapeToken(f.key), "g"), value);
  });
  return out;
};

// Shortcodes offered when composing for a specific job — the fields its service
// defines, plus the catch-all. Locked fields already have static shortcodes.
export const smsShortcodesForJob = function (defs, job) {
  if (!defs || !defs.enabled) return [];
  const out = fieldsFor(defs, resolveService(job), "clientPortal")
    .filter((f) => !f.locked)
    .map((f) => ({ code: `{${f.key}}`, label: f.label }));
  out.push({ code: JOB_DETAILS_TOKEN, label: "All of the job's details" });
  return out;
};

// Templates usable for one job. Pass no job — bulk sends, where the services
// are mixed — to get only the unscoped ones. Templates saved before service
// scoping have no `service` and are treated as unscoped.
export const templatesForJob = function (defs, templates, job) {
  const list = Array.isArray(templates) ? templates : [];
  if (!defs || !defs.enabled) return list;
  const service = job ? resolveService(job) : null;
  return list.filter((t) => !t.service || t.service === SERVICE_ALL || t.service === service);
};

// 'Core Diameter (mm)' -> 'cf_coreDiameter'
export const keyFromLabel = function (label) {
  const cleaned = String(label || "")
    .replace(/[^a-zA-Z0-9 ]/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!cleaned.length) return "";
  const camel = cleaned
    .map((word, i) =>
      i === 0
        ? word.charAt(0).toLowerCase() + word.slice(1)
        : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join("");
  return `cf_${camel}`;
};
